export interface Route {
  pattern: RegExp;
  keys: string[];
  render: (params: Record<string, string>, query: URLSearchParams) => void | Promise<void>;
  /** Giriş gerektirir mi? */
  auth: boolean;
}

/**
 * Sayfa kimliği. SPA tek bir HTML kabuğu döndürdüğü için başlık, canonical ve
 * paylaşım etiketleri sabit kalıyordu: sekiz yasal sayfa da "ben ana sayfayım"
 * diyor, sitemap'te listelenmelerine rağmen arama motoru için ayırt edilemez
 * oluyorlardı. Rota çözülürken güncelliyoruz.
 */
export interface PageMeta {
  title: string;
  /** Herkese açık olmayan sayfalar dizine girmemeli. */
  index?: boolean;
  canonical?: string;
}

import { BRAND } from './ui/brand.ts';
import { trackView } from './stats.ts';
import { UNAUTHORIZED_EVENT } from './api.ts';
import { setSession, state } from './state.ts';

const BASE_TITLE = BRAND.title;
let metaNodes: { canonical: HTMLLinkElement; robots: HTMLMetaElement; ogTitle: HTMLMetaElement } | null = null;

function ensureMetaNodes() {
  if (metaNodes) return metaNodes;
  const canonical = (document.querySelector('link[rel=canonical]') as HTMLLinkElement | null)
    ?? Object.assign(document.createElement('link'), { rel: 'canonical' });
  if (!canonical.isConnected) document.head.appendChild(canonical);
  let robots = document.querySelector('meta[name=robots]') as HTMLMetaElement | null;
  if (!robots) {
    robots = document.createElement('meta');
    robots.name = 'robots';
    document.head.appendChild(robots);
  }
  let ogTitle = document.querySelector('meta[property="og:title"]') as HTMLMetaElement | null;
  if (!ogTitle) {
    ogTitle = document.createElement('meta');
    ogTitle.setAttribute('property', 'og:title');
    document.head.appendChild(ogTitle);
  }
  metaNodes = { canonical, robots, ogTitle };
  return metaNodes;
}

export function setPageMeta(meta: PageMeta): void {
  const nodes = ensureMetaNodes();
  document.title = meta.title === BASE_TITLE ? meta.title : `${meta.title} — ${BASE_TITLE}`;
  nodes.ogTitle.setAttribute('content', document.title);
  nodes.canonical.href = meta.canonical ?? location.origin + location.pathname + location.search;
  nodes.robots.content = meta.index ? 'index, follow' : 'noindex, follow';
}

const routes: Route[] = [];
let notFoundHandler: (() => void) | null = null;
let guard: ((path: string, auth: boolean) => boolean) | null = null;

export function route(path: string, auth: boolean, render: Route['render']): void {
  const keys: string[] = [];
  const pattern = new RegExp(
    '^' + path.replace(/:([\w]+)/g, (_, key) => { keys.push(key); return '([^/]+)'; }) + '/?$',
  );
  routes.push({ pattern, keys, render, auth });
}

export function setNotFound(handler: () => void): void {
  notFoundHandler = handler;
}

export function setGuard(handler: (path: string, auth: boolean) => boolean): void {
  guard = handler;
}

export function navigate(path: string, replace = false): void {
  // Bir katman (oynatıcı) kapanırken kendi geçmiş girdisini geri alıyorsa o
  // geri adım bitmeden adres yazılmaz: yazılırsa yanlış girdinin üstüne düşer.
  afterHistorySettled(() => {
    if (replace) history.replaceState({}, '', path);
    else history.pushState({}, '', path);
    void resolve();
  });
}

export function currentPath(): string {
  return location.pathname;
}

// --- Yarış belirteci ---------------------------------------------------------
// Görünümler ortak <main>'e `await` sonrasında yazıyor. Yavaş bir isteğin yanıtı,
// kullanıcı çoktan başka ekrana geçmişken gelirse o ekranı ezmesin diye her
// çizim bir sıra numarası alır; `stale()` true ise görünüm yazmadan çıkar.
let renderSeq = 0;

export function renderGuard(): () => boolean {
  const mine = ++renderSeq;
  return () => mine !== renderSeq;
}

// --- Geçmişte duran katman (oynatıcı) ----------------------------------------
// Oynatıcı bir rota değil, sayfanın üstünde bir katman. Tarayıcının Geri tuşu
// (telefonda geri hareketi) onu kapatsın diye açılırken aynı adresle bir geçmiş
// girdisi eklenir. Geri'ye basılırsa girdi zaten gitmiştir, yalnız katman
// kapatılır; katman kendi düğmesiyle kapanırsa girdi `history.back()` ile geri
// alınır ve o adımın popstate'i yutulur. İki durumda da rota yeniden ÇİZİLMEZ.
let overlayClose: (() => void) | null = null;
let swallowPops = 0;
let settleTimer: number | null = null;
let afterSettle: Array<() => void> = [];

function flushSettled(): void {
  if (settleTimer !== null) { clearTimeout(settleTimer); settleTimer = null; }
  swallowPops = 0;
  const queue = afterSettle;
  afterSettle = [];
  for (const fn of queue) {
    try { fn(); } catch { /* bir bekleyen iş diğerlerini düşürmesin */ }
  }
}

/** Bekleyen geri adım yoksa hemen, varsa o tamamlanınca çalıştırır. */
export function afterHistorySettled(fn: () => void): void {
  if (swallowPops > 0) afterSettle.push(fn);
  else fn();
}

export function pushOverlay(close: () => void): void {
  overlayClose = close;
  afterHistorySettled(() => {
    // Beklerken katman kapandıysa girdi eklenmez.
    if (overlayClose !== close) return;
    try { history.pushState({ overlay: true }, ''); } catch { overlayClose = null; }
  });
}

/** Katman kendi kendine kapandı: eklenen girdiyi geri al. */
export function popOverlay(): void {
  if (!overlayClose) return;
  overlayClose = null;
  if (swallowPops > 0) return; // önceki geri adım hâlâ yolda; girdi henüz eklenmedi
  swallowPops = 1;
  // popstate hiç gelmezse (geçmiş boş, gömülü görünüm) bekleyenler kilitlenmesin.
  settleTimer = window.setTimeout(flushSettled, 700);
  try { history.back(); } catch { flushSettled(); }
}

function onPopState(): void {
  if (swallowPops > 0) { flushSettled(); return; }
  if (overlayClose) {
    const close = overlayClose;
    overlayClose = null;
    close();
    return;
  }
  void resolve();
}

export async function resolve(): Promise<void> {
  renderSeq += 1;
  const path = location.pathname;
  const query = new URLSearchParams(location.search);
  for (const item of routes) {
    const match = item.pattern.exec(path);
    if (!match) continue;
    if (guard && !guard(path, item.auth)) return;
    const params: Record<string, string> = {};
    item.keys.forEach((key, index) => { params[key] = decodeURIComponent(match[index + 1]); });
    await item.render(params, query);
    trackView(location.pathname);
    return;
  }
  notFoundHandler?.();
}

export function startRouter(): void {
  window.addEventListener('popstate', onPopState);
  // Sunucu oturumu tanımıyor (401): yerel oturumu düşür ve girişe götür. Yoksa
  // her ekran ayrı ayrı hata gösteriyor, kullanıcı elle yenilemek zorunda kalıyordu.
  window.addEventListener(UNAUTHORIZED_EVENT, () => {
    if (!state.session) return;
    const next = location.pathname + location.search;
    setSession(null);
    // Bir tur beklenir: aynı olayı dinleyen oynatıcı önce kapanıp kendi geçmiş
    // girdisini geri almaya başlasın, adres ondan sonra yazılsın.
    window.setTimeout(() => navigate(`/login?next=${encodeURIComponent(next)}`, true), 0);
  });
  // Uygulama içi bağlantılar tam sayfa yükleme yapmasın.
  document.addEventListener('click', (event) => {
    // Yeni sekmede açma (Ctrl/⌘/Shift + tık) tarayıcıya bırakılır.
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = (event.target as HTMLElement | null)?.closest('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (!href || !href.startsWith('/') || anchor.hasAttribute('target') || anchor.hasAttribute('download')) return;
    // `//alan.adi` başka sitedir; `/api/…` sunucunun kendi sayfasıdır (ödeme).
    if (href.startsWith('//') || href.startsWith('/api/')) return;
    event.preventDefault();
    navigate(href);
  });
  void resolve();
}
